/**
 * Pure helper functions shared by the Profile / billing UI.
 * Kept free of React and API calls so they're trivial to unit test.
 */

/** Format paise as an INR currency string. Shows up to 2 decimals only
 *  when needed, so 50 paise renders as ₹0.5 instead of rounding to ₹1. */
export function paiseToINR(paise) {
  if (paise == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(paise / 100);
}

/**
 * True when `candidatePlan` is the plan the app is currently on.
 *
 * An app with no explicit plan assigned yet (`currentPlan` is null/undefined)
 * is implicitly on the platform's default plan (plans.is_default), so that
 * plan should still be marked current instead of no card ever matching.
 * Matching by price_paise === 0 instead of is_default would mark *every*
 * zero-priced plan as current whenever there's more than one free-tier plan.
 */
export function isPlanCurrent(currentPlan, candidatePlan) {
  if (!candidatePlan) return false;
  return currentPlan
    ? currentPlan.id === candidatePlan.id
    : !!candidatePlan.is_default;
}

/**
 * Base/GST/total breakdown for display on a plan card, before checkout.
 * Mirrors the exact server-side formula in routes/billing.js POST /checkout
 * (gstAmountPaise = round(base * gstPercentage / 100)) — this is a display
 * preview only; the real charge is always computed server-side at checkout.
 */
export function calculateGstBreakdown(pricePaise, gstPercentage, memberCount = 1) {
  const basePaise = (pricePaise || 0) * (memberCount || 1);
  const gstAmountPaise = Math.round(basePaise * (gstPercentage || 0) / 100);
  return { basePaise, gstPercentage: gstPercentage || 0, gstAmountPaise, totalPaise: basePaise + gstAmountPaise };
}
